import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Building2, Users, FileText, Video, CreditCard, ToggleLeft, ToggleRight,
    Pencil, TrendingUp, TrendingDown, CheckCircle2, XCircle, Clock, AlertTriangle, RefreshCw,
    Calendar, ChevronDown, Save, X, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    getCompanyById, getActivePlans, toggleCompanyStatus, updateSubscription, updateCompany, applyAddon,
    getCompanyUsageHistory,
} from '../services/apiClient';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import CompanyProfilePanel from '@/components/superadmin/CompanyProfilePanel';
import CompanyBillingPanel from '@/components/superadmin/CompanyBillingPanel';
import ResumeAnalyticsPanel from '@/components/superadmin/ResumeAnalyticsPanel';
import UsageHistoryTable from '@/components/billing/UsageHistoryTable';

const CURRENCY = '₹';

const STATUS_CONFIG = {
    trial: { label: 'Trial', color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30', icon: Clock },
    active: { label: 'Active', color: 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30', icon: CheckCircle2 },
    grace_period: { label: 'Grace Period', color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30', icon: AlertTriangle },
    expired: { label: 'Expired', color: 'bg-destructive/15 text-destructive border-destructive/30', icon: XCircle },
    cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground border-border', icon: XCircle },
};

const LOG_ACTION_LABELS = {
    assigned: 'Plan Assigned',
    upgraded: 'Plan Upgraded',
    downgraded: 'Plan Downgraded',
    trial_started: 'Trial Started',
    activated: 'Activated',
    cancelled: 'Cancelled',
    expired: 'Expired',
    reactivated: 'Reactivated',
    trial_extended: 'Trial Extended',
};

function UsageBar({ label, used, limit }) {
    const unlimited = limit === -1;
    const pct = unlimited ? 0 : Math.min((used / limit) * 100, 100);
    const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-primary';
    return (
        <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
                <span>{label}</span>
                <span className="font-medium text-foreground">{unlimited ? `${used} / ∞` : `${used} / ${limit}`}</span>
            </div>
            {!unlimited && (
                <div className="h-1.5 rounded-full bg-muted">
                    <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

function StatusBadgeComp({ status }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.expired;
    const Icon = cfg.icon;
    return (
        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium', cfg.color)}>
            <Icon size={11} /> {cfg.label}
        </span>
    );
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysLeft(dateStr) {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
}

function SubscriptionCard({ company, plan, stats, onChangePlan, onApplyAddon, onRefresh }) {
    const sub = company.subscription || {};
    const trialDays = sub.status === 'trial' ? daysLeft(sub.trialEndDate) : null;
    const graceDays = sub.status === 'grace_period' ? daysLeft(sub.gracePeriodEndDate) : null;
    const hasAddon = (sub.addonInterviewCredits || 0) > 0 || (sub.addonActiveJobCredits || 0) > 0;

    return (
        <div className="rounded-xl border border-border bg-muted/40 p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Current Plan</p>
                    <p className="text-xl font-bold mt-0.5">{plan?.name || '—'}</p>
                    {plan && <p className="text-primary font-semibold">{CURRENCY}{plan.price?.toLocaleString('en-IN')}/mo</p>}
                    {hasAddon && (
                        <p className="text-xs text-primary mt-0.5">
                            Add-On: {sub.addonInterviewCredits > 0 ? `+${sub.addonInterviewCredits} interviews` : ''}{sub.addonActiveJobCredits > 0 ? ` +${sub.addonActiveJobCredits} jobs` : ''}
                        </p>
                    )}
                </div>
                <div className="flex flex-col items-end gap-2">
                    <StatusBadgeComp status={sub.status} />
                    <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={onApplyAddon} className="text-xs gap-1">
                            <Package size={12} /> Add-On
                        </Button>
                        <Button size="sm" variant="outline" onClick={onChangePlan} className="text-xs gap-1">
                            <TrendingUp size={12} /> Change Plan
                        </Button>
                    </div>
                </div>
            </div>

            {sub.status === 'trial' && trialDays !== null && (
                <div className={cn('rounded-lg px-3 py-2 text-sm flex items-center gap-2', trialDays <= 3 ? 'bg-destructive/10 border border-destructive/30 text-destructive' : 'bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:text-blue-400')}>
                    <Clock size={14} />
                    {trialDays > 0 ? `${trialDays} day${trialDays !== 1 ? 's' : ''} left in trial (ends ${formatDate(sub.trialEndDate)})` : 'Trial has ended — grace period starting'}
                </div>
            )}
            {sub.status === 'grace_period' && graceDays !== null && (
                <div className="rounded-lg px-3 py-2 text-sm bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <AlertTriangle size={14} />
                    Grace period: {graceDays > 0 ? `${graceDays} day${graceDays !== 1 ? 's' : ''} remaining` : 'Ends today'} — account will be blocked after this.
                </div>
            )}
            {(sub.status === 'expired' || sub.status === 'cancelled') && (
                <div className="rounded-lg px-3 py-2 text-sm bg-destructive/10 border border-destructive/30 text-destructive flex items-center gap-2">
                    <XCircle size={14} /> Account is blocked. Please assign an active plan to reactivate.
                </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground border-t border-border pt-4">
                <div><p>Trial Started</p><p className="text-foreground font-medium mt-0.5">{formatDate(sub.trialStartDate)}</p></div>
                <div><p>Trial Ends</p><p className="text-foreground font-medium mt-0.5">{formatDate(sub.trialEndDate)}</p></div>
                <div><p>Period Start</p><p className="text-foreground font-medium mt-0.5">{formatDate(sub.currentPeriodStart)}</p></div>
                <div><p>Usage Resets</p><p className="text-foreground font-medium mt-0.5">{formatDate(sub.usageResetDate)}</p></div>
            </div>

            {plan && (
                <div className="border-t border-border pt-4 space-y-3">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Usage</p>
                    <UsageBar label="AI Interviews" used={sub.interviewsUsedThisMonth || 0} limit={plan.limits?.maxInterviewsPerMonth} />
                    <UsageBar label="Active Jobs" used={stats?.activeJobs || 0} limit={plan.limits?.maxActiveJobs} />
                    <UsageBar label="Team Members" used={(stats?.totalUsers || 0) - (stats?.usersByRole?.candidate || 0)} limit={plan.limits?.maxUsers} />
                </div>
            )}
        </div>
    );
}

function ApplyAddonModal({ open, onClose, companyId, onApplied }) {
    const [quantity, setQuantity] = useState(1);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    const handleApply = async () => {
        setLoading(true);
        try {
            const res = await applyAddon(companyId, { quantity, notes });
            toast.success(res.message || 'Add-on applied.');
            setQuantity(1);
            setNotes('');
            onApplied();
            onClose();
        } catch (err) {
            toast.error(err.message || 'Failed to apply add-on.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Apply Add-On Interview Pack</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2 text-sm">
                    <div className="rounded-lg border border-primary/30 bg-primary/8 p-4 space-y-2">
                        <div className="flex items-center gap-2">
                            <Package size={16} className="text-primary" />
                            <span className="font-semibold">Add-On Pack × {quantity}</span>
                            <span className="ml-auto text-primary font-bold">₹{(6000 * quantity).toLocaleString('en-IN')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">+{100 * quantity} interviews &amp; +{2 * quantity} active jobs added to company limits.</p>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Quantity</Label>
                        <div className="flex items-center gap-3">
                            <Button size="sm" variant="outline" className="w-8 h-8 p-0" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</Button>
                            <span className="text-lg font-bold w-6 text-center">{quantity}</span>
                            <Button size="sm" variant="outline" className="w-8 h-8 p-0" onClick={() => setQuantity(q => q + 1)}>+</Button>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Notes (optional)</Label>
                        <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Monthly top-up..." className="bg-muted/40 border-border text-sm" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button onClick={handleApply} disabled={loading}>
                        {loading ? 'Applying...' : 'Apply Add-On'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ChangePlanModal({ open, onClose, companyId, currentPlanId, onChanged }) {
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [action, setAction] = useState('upgrade');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            getActivePlans().then(r => setPlans(r.data || []));
            setSelectedPlanId(currentPlanId || '');
            setNotes('');
        }
    }, [open, currentPlanId]);

    const handleSave = async () => {
        if (!selectedPlanId) { toast.error('Please select a plan.'); return; }
        setLoading(true);
        try {
            await updateSubscription(companyId, { planId: selectedPlanId, action, notes });
            toast.success('Subscription updated.');
            onChanged();
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const CURRENCY_SYMBOL = '₹';

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Change Subscription Plan</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        {plans.map(p => (
                            <button key={p._id} onClick={() => setSelectedPlanId(p._id)} className={cn('w-full text-left rounded-lg border p-3 transition-all', selectedPlanId === p._id ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground/40')}>
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold">{p.name}</span>
                                    <span className="text-primary font-bold">{CURRENCY_SYMBOL}{p.price?.toLocaleString('en-IN')}/mo</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                            </button>
                        ))}
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Action</Label>
                        <select value={action} onChange={e => setAction(e.target.value)} className="w-full rounded-md bg-muted/40 border border-border text-foreground text-sm px-3 py-2">
                            <option value="upgrade">Upgrade</option>
                            <option value="downgrade">Downgrade</option>
                            <option value="activate">Activate (from trial/expired)</option>
                            <option value="reactivate">Reactivate</option>
                            <option value="extend_trial">Extend Trial (+14 days)</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Notes (optional)</Label>
                        <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Reason for change..." className="bg-muted/40 border-border" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditCompanyModal({ open, onClose, company, onSaved }) {
    const [form, setForm] = useState({ companyName: '', contactEmail: '', description: '' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (company) setForm({ companyName: company.companyName || '', contactEmail: company.contactEmail || '', description: company.description || '' });
    }, [company]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await updateCompany(company.companyId, form);
            toast.success('Company details updated.');
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader><DialogTitle>Edit Company Details</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="space-y-1"><Label>Company Name</Label><Input value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} className="bg-muted/40 border-border" /></div>
                    <div className="space-y-1"><Label>Contact Email</Label><Input value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} className="bg-muted/40 border-border" /></div>
                    <div className="space-y-1"><Label>Description</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full rounded-md bg-muted/40 border border-border text-foreground text-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const ROLE_COLORS = { admin: 'bg-purple-500/15 text-purple-600 dark:text-purple-400', interviewer: 'bg-blue-500/15 text-blue-600 dark:text-blue-400', hr_manager: 'bg-primary/15 text-primary', candidate: 'bg-green-500/15 text-green-600 dark:text-green-400', ml_engineer: 'bg-orange-500/15 text-orange-600 dark:text-orange-400' };

export default function SuperAdminCompanyDetailPage() {
    const { companyId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [changePlanOpen, setChangePlanOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [addonOpen, setAddonOpen] = useState(false);
    const [toggling, setToggling] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getCompanyById(companyId);
            setData(res.data);
        } catch {
            toast.error('Failed to load company details.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleToggleStatus = async () => {
        setToggling(true);
        try {
            await toggleCompanyStatus(companyId);
            toast.success(`Company ${data.company.isActive ? 'deactivated' : 'activated'}.`);
            fetchData();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setToggling(false);
        }
    };

    if (loading) return (
        <div className="p-6  mx-auto space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />)}
        </div>
    );

    if (!data) return (
        <div className="p-6 text-center text-muted-foreground">Company not found.</div>
    );

    const { company, plan, statistics: stats, subscriptionLogs, recentUsers, recentTemplates, recentSessions, setup } = data;

    return (
        <div className="mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin/companies')} className="gap-1 text-muted-foreground hover:text-foreground shrink-0 mt-1">
                    <ArrowLeft size={16} /> Back
                </Button>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-bold truncate">{company.companyName}</h1>
                        <Badge variant="outline" className="font-mono text-xs">{company.companyId}</Badge>
                        <Badge className={company.isActive ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-destructive/15 text-destructive border-destructive/30'}>
                            {company.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{company.contactEmail}</p>
                    {company.description && <p className="text-sm text-muted-foreground">{company.description}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="gap-1">
                        <Pencil size={14} /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleToggleStatus} disabled={toggling} className={cn('gap-1', company.isActive ? 'text-destructive hover:bg-destructive/10' : 'text-green-600 dark:text-green-400 hover:bg-green-500/10')}>
                        {company.isActive ? <ToggleLeft size={14} /> : <ToggleRight size={14} />}
                        {company.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={fetchData}><RefreshCw size={14} /></Button>
                </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                    { label: 'Total Users', value: stats.totalUsers, icon: Users },
                    { label: 'Templates', value: stats.totalTemplates, icon: FileText },
                    { label: 'Total Sessions', value: stats.totalSessions, icon: Video },
                    { label: 'Completed', value: stats.sessionsByStatus?.completed, icon: CheckCircle2 },
                ].map(kpi => (
                    <div key={kpi.label} className="rounded-xl border border-border bg-muted/40 p-4">
                        <p className="text-xs text-muted-foreground">{kpi.label}</p>
                        <p className="text-2xl font-bold mt-1">{kpi.value ?? '—'}</p>
                    </div>
                ))}
            </div>

            {/* Main tabs */}
            <Tabs defaultValue="overview">
                <TabsList className="bg-muted/40 border border-border">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="profile">Profile{setup ? ` (${setup.percent}%)` : ''}</TabsTrigger>
                    <TabsTrigger value="users">Users ({stats.totalUsers})</TabsTrigger>
                    <TabsTrigger value="templates">Templates ({stats.totalTemplates})</TabsTrigger>
                    <TabsTrigger value="sessions">Sessions ({stats.totalSessions})</TabsTrigger>
                    <TabsTrigger value="resumes">Resumes</TabsTrigger>
                    <TabsTrigger value="billing">Billing</TabsTrigger>
                    <TabsTrigger value="history">Subscription History</TabsTrigger>
                </TabsList>

                <TabsContent value="resumes" className="mt-4">
                    <ResumeAnalyticsPanel companyId={company.companyId} />
                </TabsContent>

                <TabsContent value="billing" className="mt-4">
                    <CompanyBillingPanel company={company} onChanged={fetchData} />
                </TabsContent>

                <TabsContent value="profile" className="mt-4">
                    <CompanyProfilePanel company={company} setup={setup} />
                </TabsContent>

                <TabsContent value="overview" className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <SubscriptionCard
                        company={company}
                        plan={plan}
                        stats={stats}
                        onChangePlan={() => setChangePlanOpen(true)}
                        onApplyAddon={() => setAddonOpen(true)}
                        onRefresh={fetchData}
                    />
                    <div className="rounded-xl border border-border bg-muted/40 p-5 space-y-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">User Breakdown</p>
                        {Object.entries(stats.usersByRole || {}).map(([role, count]) => (
                            <div key={role} className="flex items-center justify-between">
                                <span className={cn('text-xs px-2 py-0.5 rounded-full capitalize', ROLE_COLORS[role] || 'bg-muted text-muted-foreground')}>{role.replace('_', ' ')}</span>
                                <span className="font-bold">{count}</span>
                            </div>
                        ))}
                        <div className="border-t border-border pt-3 text-xs text-muted-foreground">
                            <div className="flex justify-between"><span>Created</span><span>{formatDate(company.createdAt)}</span></div>
                            <div className="flex justify-between mt-1"><span>Last Updated</span><span>{formatDate(company.updatedAt)}</span></div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="users" className="mt-4">
                    <div className="rounded-xl border border-border bg-muted/40 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-muted/40">
                                <tr>
                                    {['Name', 'Email', 'Role', 'Status', 'Joined'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {recentUsers?.map(u => (
                                    <tr key={u._id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                                        <td className="px-4 py-3 font-medium">{u.firstName} {u.lastName}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                                        <td className="px-4 py-3"><span className={cn('text-xs px-2 py-0.5 rounded-full capitalize', ROLE_COLORS[u.role] || 'bg-muted text-muted-foreground')}>{u.role?.replace('_', ' ')}</span></td>
                                        <td className="px-4 py-3"><span className={cn('text-xs px-2 py-0.5 rounded-full', u.isActive ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-destructive/15 text-destructive')}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                                        <td className="px-4 py-3 text-muted-foreground">{formatDate(u.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!recentUsers?.length && <p className="text-center py-8 text-muted-foreground text-sm">No users found.</p>}
                    </div>
                </TabsContent>

                <TabsContent value="templates" className="mt-4">
                    <div className="rounded-xl border border-border bg-muted/40 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-muted/40">
                                <tr>
                                    {['Title', 'Status', 'Created'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {recentTemplates?.map(t => (
                                    <tr key={t._id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                                        <td className="px-4 py-3 font-medium">{t.title}</td>
                                        <td className="px-4 py-3"><span className={cn('text-xs px-2 py-0.5 rounded-full', t.isActive ? 'bg-green-500/15 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground')}>{t.isActive ? 'Active Job' : 'Closed'}</span></td>
                                        <td className="px-4 py-3 text-muted-foreground">{formatDate(t.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!recentTemplates?.length && <p className="text-center py-8 text-muted-foreground text-sm">No templates found.</p>}
                    </div>
                </TabsContent>

                <TabsContent value="sessions" className="mt-4">
                    <div className="rounded-xl border border-border bg-muted/40 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="border-b border-border bg-muted/40">
                                <tr>
                                    {['Candidate', 'Template', 'Interviewer', 'Status', 'Date'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {recentSessions?.map(s => (
                                    <tr key={s._id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                                        <td className="px-4 py-3">{s.candidate ? `${s.candidate.firstName} ${s.candidate.lastName}` : '—'}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{s.template?.title || '—'}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{s.interviewer ? `${s.interviewer.firstName} ${s.interviewer.lastName}` : '—'}</td>
                                        <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize">{s.status}</span></td>
                                        <td className="px-4 py-3 text-muted-foreground">{formatDate(s.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!recentSessions?.length && <p className="text-center py-8 text-muted-foreground text-sm">No sessions found.</p>}
                    </div>
                </TabsContent>

                <TabsContent value="history" className="mt-4 space-y-4">
                    <UsageHistoryTable
                        fetcher={() => getCompanyUsageHistory(companyId)}
                        title="Usage by billing period"
                    />
                    <div className="space-y-3">
                        {subscriptionLogs?.map(log => (
                            <div key={log._id} className="rounded-xl border border-border bg-muted/40 p-4">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-semibold text-sm">{LOG_ACTION_LABELS[log.action] || log.action}</span>
                                    <span className="text-xs text-muted-foreground">{formatDate(log.createdAt)}</span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {log.previousPlanId && <span className="text-destructive">{log.previousPlanId.name}</span>}
                                    {log.previousPlanId && log.planId && <span className="mx-1">→</span>}
                                    {log.planId && <span className="text-green-600 dark:text-green-400">{log.planId.name}</span>}
                                    {log.performedBy && <span className="ml-2 text-muted-foreground">by {log.performedBy.firstName} {log.performedBy.lastName}</span>}
                                </div>
                                {log.notes && <p className="text-xs text-muted-foreground mt-1 italic">{log.notes}</p>}
                            </div>
                        ))}
                        {!subscriptionLogs?.length && <p className="text-center py-8 text-muted-foreground text-sm">No subscription history found.</p>}
                    </div>
                </TabsContent>
            </Tabs>

            <ApplyAddonModal
                open={addonOpen}
                onClose={() => setAddonOpen(false)}
                companyId={companyId}
                onApplied={fetchData}
            />
            <ChangePlanModal
                open={changePlanOpen}
                onClose={() => setChangePlanOpen(false)}
                companyId={companyId}
                currentPlanId={company.subscription?.planId}
                onChanged={fetchData}
            />
            <EditCompanyModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                company={company}
                onSaved={fetchData}
            />
        </div>
    );
}
